import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import { 
  getBasicInfo,
  setBasicInfo,
  setSessionStorageItem,
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_EMAIL_CAPTURE,
  SESSION_STORAGE_SAVED_QUOTE_DATA,
} from '../../../../sessionStorage';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string, date, boolean } from '../../../Helpers/Validators/customValidators';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import States, { compare } from '../../../Helpers/Lists/States';
import { InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput } from '../../../Helpers/Inputs';
import BackNext from '../../Common/SubComponents/BackNext';
import GenderButtons from '../../Common/SubComponents/GenderButtons';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.jsx';

import '../../Common/BasicInfo.scss';

const BasicInfo = forwardRef((props, ref) => {
  const innerRef = useRef(null);
  const emailCapture = getSessionStorageItem(SESSION_STORAGE_EMAIL_CAPTURE);
  const { isPartnerReferralEmailCaptureActive, enablePartnerReferralEmailCapture } = emailCapture;

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const savedQuote = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
  const accountProductTypeSettings = props ? accountObj.accountProducts.find(product => product.productLandingUrl === '/landing/supplemental') : null;
  const basicInfo = getBasicInfo();

  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(savedQuote);
  const [isQuoteSaved, setQuoteSaved] = useState(savedQuote.quoteSaved);

  var states = States();
  states.push({ label: "Puerto Rico", value: "PR" });
  states = states.sort(compare);

  const [noValidate] = useState(props.noValidate || false);

  const formMethods = useForm({
    resolver: yupResolver(BasicInfo.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });

  const { handleSubmit, trigger, register, setValue } = formMethods;

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const setCurrentSavedQuoteData = () => {
    const newData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSaveQuoteContact(newData);
  };
  
  useEffect(() => {
    setSaveQuoteContact(props.saveQuoteContact);
  }, [props.saveQuoteContact]);

  useEffect(() => {
    if (submitDisabled) {
      props.setEmailCaptureActive(submitDisabled);
    }
  }, [submitDisabled]);

  useEffect(() => {
    if (saveQuoteContact.emailAddress !== "") {
      setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
        "quoteSaved": isQuoteSaved,
        "firstName": saveQuoteContact.firstName,
        "lastName": saveQuoteContact.lastName,
        "emailAddress": saveQuoteContact.emailAddress,
      });
    }
    setCurrentSavedQuoteData();
  }, [isQuoteSaved]);

  useEffect(() => {
    if (noValidate) return;
    // manually register fields for validation that don't play nice with react-hook-form register
    register('require');
    setValue('require', true);
    register('noValidate');
    setValue('noValidate', noValidate);

    setSubmitDisabled(!!isPartnerReferralEmailCaptureActive && !!enablePartnerReferralEmailCapture);
    if (submitDisabled) {
      props.setEmailCaptureActive(submitDisabled);
    }
  }, []);

  useImperativeHandle(ref, () =>({
    onSubmit,
    dispatchSubmit: () => {
      innerRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    get element() {
      return innerRef.current;
    }
  }));

  const covrDisclaimer = 'Insurance products are sold through Covr Financial Technologies Inc ("Covr"). Covr sells insurance as an agent or broker by contractual agreements with its insurance carrier providers. Covr is licensed in the following states: Delaware (3000067942), Idaho (607086), California (0L77229).';

  useEffect(() => {
    const quoteData = {
      accountId: accountObj.account.accountId,
      accountName: accountObj.account.accountName,
      oneClickAccountId: accountObj.account.oneClickAccountId,
      accountProductTypeSettingId: accountProductTypeSettings.accountProductTypeSettingId,
      productType: "supplemental",
      sessionId: accountObj.sessionId,
      accountColorLogoUrl: accountObj.account.logoUrl,
      firstName: saveQuoteContact.firstName,
      lastName: saveQuoteContact.lastName,
      emailAddress: saveQuoteContact.emailAddress,
      gender: props.formState.gender,
      dateOfBirth: basicInfo.birthdate,
      state: basicInfo.state,
      height: null,
      weight: null,
      tobaccoStatus: false,
      coverageAmount: 0,
      termLength: 0,
      healthClass: "PP",
      quoteLowestPrice: null,
      quoteFastestProcess: null,
      quoteMostPopular: null,
      quotedLowestPrice: null,
      signatureEmail: "insuranceapplication@covrtech.com",
      signaturePhone: accountObj.account.phone,
      covrDisclaimer: covrDisclaimer,
      disclaimerHtml: accountObj.account.disclaimerHtml ? accountObj.account.disclaimerHtml : null,
    };
    setSaveQuoteData(quoteData);
  }, [saveQuoteContact]);

  useEffect(() => {
      if (submitDisabled) {
        props.setSaveQuoteData(saveQuoteData);
      }
  }, [saveQuoteData]);

  const onSubmit = () => {
    if (props.onDeclined != null) {
      props.onDeclined();
    } else {
      // Save formState to basicinfo
      var basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.firstName = saveQuoteContact.firstName;
      basicInfoFromSessionStorage.lastName = saveQuoteContact.lastName;
      basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
      basicInfoFromSessionStorage.state = props.formState.state;
      basicInfoFromSessionStorage.usResident = props.formState.usResident;

      setBasicInfo(basicInfoFromSessionStorage);

      if (submitDisabled && isPartnerReferralEmailCaptureActive) {
        props.setModalSaveQuoteIsOpen(submitDisabled);
      }

      props.onNext();
    }
  }

  return (
    <div className="basic-info d-flex justify-content-center">
      {noValidate ?
        <BasicInfoForm formState={props.formState} handleValueChange={props.handleValueChange} onFieldChange={props.onFieldChange} hideBackNext={props.hideBackNext} logoBottom={props.logoBottom} states={states} />
        :
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" ref={innerRef}>
            <BasicInfoForm formState={props.formState} handleValueChange={handleValueChange} onFieldChange={props.onFieldChange} hideBackNext={props.hideBackNext} logoBottom={props.logoBottom} states={states} />
          </Form>
        </FormProvider>
      }
    </div>
  );
});

export default BasicInfo;

function BasicInfoForm(props) {
  return (
    <div className="d-flex flex-column">
      <h2 className={`mx-auto ${!props.logoBottom ? 'd-none' : ''}`}>Coverage Details</h2>

      <div className={`d-flex justify-content-center ${props.logoBottom ? 'order-5' : 'order-1'}`}>
        <img src="/product-landing/supp/aflac-logo.png" alt="Aflac Logo" style={{ height: '73px', width: '160px' }} className="mb-2" />
      </div>

      <div className={`m-0 ${props.logoBottom ? 'order-1' : 'order-2'}`}>
        <GenderButtons
          name='gender'
          selectedGender={props.formState.gender}
          onClickMale={() => props.handleValueChange('gender', 'M')}
          onClickFemale={() => props.handleValueChange('gender', 'F')}
        />
      </div>

      <div className={`mb-3 ${props.logoBottom ? 'order-2' : 'order-3'}`}>
        <TextInput
          name="birthdate"
          id="birthdate"
          label="Date of Birth"
          value={props.formState.birthdate}
          onChange={(e) => props.handleValueChange('birthdate', e.target.value)}
          InputProps={{
            inputComponent: InputMaskDate,
          }}
        />
      </div>

      <div className={`mb-3 ${props.logoBottom ? 'order-3' : 'order-4'}`}>
        <SelectInput
          name="state"
          id="state"
          value={props.formState.state}
          onChange={(e) => props.handleValueChange('state', e.target.value)}
          label="State"
          selectOptions={props.states}
        />
      </div>


      <p className={props.logoBottom ? 'text-center order-last' : 'd-none'}>"We help with expenses health insurance doesn't cover so you can focus on recovery."</p>

      {!props.hideBackNext &&
        <BackNext
          hideBack={true}
          onBack={props.onBack}
          hide={props.hideBackNext}
          className="order-last"
          continueLabel={{ text: "Get a Quote" }}
        />
      }
    </div>
  );
}

BasicInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func,
  onFieldChange: PropTypes.func.isRequired,
  hideBackNext: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired,
  logoBottom: PropTypes.bool,
  noValidate: PropTypes.bool,
  setModalSaveQuoteIsOpen: PropTypes.func,
  setSaveQuoteData: PropTypes.func,
  saveQuoteContact: PropTypes.object,
  emailCapture: PropTypes.object,
  setEmailCaptureActive: PropTypes.func,
  emailCaptureActive: PropTypes.bool,
};

BasicInfoForm.propTypes = {
  formState: PropTypes.object.isRequired,
  handleValueChange: PropTypes.func.isRequired,
  hideBackNext: PropTypes.bool,
  logoBottom: PropTypes.bool,
};

BasicInfo.ValidationSchema = (ageSpecs) => {
  return object().shape({
    require: boolean(),
    gender: string()
      .when('require', {
        is: true,
        then: (schema) => schema.required('Gender is required.'),
        otherwise: (schema) => schema.notRequired()
      }),
    birthdate: date()
      .transform(emptyStringToUndefined)
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min }), `Minimum age is ${ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .when('require', {
        is: true,
        then: (schema) => schema.transform(emptyStringToUndefined).typeError('Invalid date.').required('Birthdate is required.'),
        otherwise: (schema) => schema.transform(emptyStringToUndefined).notRequired()
      })
    ,
    state: string()
      .when('require', {
        is: true,
        then: (schema) => schema.required('State is required.'),
        otherwise: (schema) => schema.notRequired()
      }),
  })
};

BasicInfo.DefaultFormState = (prefill = false, existingBasicInfo = null) => {
  return {
    gender: prefill ? 'M' : (existingBasicInfo && existingBasicInfo.gender) || '',
    birthdate: prefill ? '11/15/1970' : (existingBasicInfo && existingBasicInfo.birthdate) || '',
    state: prefill ? 'ID' : (existingBasicInfo && existingBasicInfo.state) || '',
    usResident: true,
  };
};
